import {
  isYouTubeProvider,
  MediaPlayer,
  MediaProvider,
  MediaProviderAdapter,
  Poster,
  Track,
  type MediaPlayerInstance,
} from '@vidstack/react';
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from '@vidstack/react/player/layouts/default';
import '@vidstack/react/player/styles/default/layouts/video.css';
import '@vidstack/react/player/styles/default/theme.css';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useAnalytics } from '../../../AppAnalyticsProvider';
import { useAppState } from "../../../AppListenerProvider";
import { AppContext } from "../../../AppStateProvider";
import { decimalToAspectRatio } from "../../../services/utils";
import { useVolume } from "../../../VolumeProvider";
import CustomMuteButton from "../CustomMuteButton/CustomMuteButton";
import CustomSettingsMenu from "../CustomSettingsMenu/CustomSettingsMenu";
import FullscreenPlayControl from "../FullscreenPlayControl/FullscreenPlayControl";
import { VideoProvider } from "../VideoContextManager/VideoContextManager";
import YoutubePlayerControl from "../YoutubePlayerControl/YoutubePlayerControl";
import "./AutoVideoPlayer.css";

interface ContainerProps {
  src: string;
  thumbnail?: string;
  subtitles?: string;
  isCapacitor: boolean;
  threshold: number;
  isHorizontal: boolean;
  isExternal: boolean;
  isYoutube: boolean;
  isVideoRestricted: boolean;
  isScrolling: boolean;
  scrollSpeed: number;
  aspectRatio: number;
  // When something is presented over this player (e.g. the Story Studio modal),
  // force it muted regardless of the user's global volume so it doesn't bleed
  // audio behind the overlay. Non-persisting — it never touches VolumeProvider.
  forceMuted?: boolean;
  // Show the player controls (mute/unmute, settings) immediately instead of
  // waiting for the first tap. Used on screens like upload-success where the
  // rider needs to see the unmute affordance right away to confirm their clip
  // has sound. Defaults to false so feed playback is unchanged.
  controlsInitiallyVisible?: boolean;
}

const playingScrollSpeedMax = 0.95; // pixels per millisecond

const AutoVideoPlayer: React.FC<ContainerProps> = (props) => {
  const { isNative, platform } = useAppState();
  const { state, dispatch } = useContext(AppContext);
  const { trackEvent } = useAnalytics();

  const [src, setSrc] = useState<string>(props.src);

  const [threshold, setThreshold] = useState<number | undefined>(undefined);
  const [playerWidth, setPlayerWidth] = useState("100%");
  const [isReady, setIsReady] = useState(false);
  const [shouldBePlaying, setShouldBePlaying] = useState(false);
  const playerRef = useRef<MediaPlayerInstance>(null);
  const [hasInteracted, setHasInteracted] = useState(!!props.controlsInitiallyVisible);
  const aspectRatioFraction = decimalToAspectRatio(props.aspectRatio);
  const { isMuted, setMuted } = useVolume();

  // Primary observer with your existing threshold for detecting when video should play
  const { ref: inViewRef, inView } = useInView({
    threshold: threshold, // Your high threshold (~85%)
  });

  // Secondary observer with threshold 0 to detect when element is completely out of view
  const { ref: completelyOutOfViewRef, inView: isAnyPartVisible } = useInView({
    threshold: 0, // Will be true as long as any part of the element is visible
  });

  // Combine your refs to apply both to the same element
  const combinedRef = useCallback(
    (node: any) => {
      inViewRef(node);
      completelyOutOfViewRef(node);
    },
    [inViewRef, completelyOutOfViewRef]
  );

  const shouldLoad = useMemo(() => {
    return inView && (!props.isScrolling || props.scrollSpeed < playingScrollSpeedMax);
  }, [inView, props.isScrolling, props.scrollSpeed, playingScrollSpeedMax]);

  const loadingClass = props.isExternal ? "" : !isReady ?
    "animate-[pulse_4.5s_ease-in-out_infinite] opacity-50" :
    "opacity-100 transition-opacity duration-300";


  useEffect(() => {
    if (props.threshold !== undefined) {
      setThreshold(props.threshold);
    }
  }, [props.threshold]);

  useEffect(() => {
    if (shouldLoad && playerRef.current) {
      setSrc(props.src);
      playerRef.current?.startLoading();
    } else if (!isAnyPartVisible && playerRef.current) {
      // Only unload when completely out of view (not even a single pixel visible)
      playerRef.current.pause();
      setSrc("");
    }
  }, [inView, isAnyPartVisible, props.isScrolling, props.scrollSpeed]);

  useEffect(() => {
    const shouldPlay = isReady && shouldLoad;
    setShouldBePlaying(shouldPlay);

    if (shouldPlay) {
      const handleAnyInteraction = (e: Event) => {

        const mediaPlayerElement = playerRef.current?.el;
        if (mediaPlayerElement?.contains(e.target as Node)) {
          console.log("Interaction detected within video player, ignoring");
          return;
        }

        if (playerRef.current && playerRef.current.paused) {
          console.log("User interaction detected, attempting to play video");

          playerRef.current.play()
            .then(() => {
              console.log("Successfully played video after user interaction");
              // Only remove listeners if we succeed
              document.removeEventListener('click', handleAnyInteraction);
              document.removeEventListener('touchstart', handleAnyInteraction);
              document.removeEventListener('scroll', handleAnyInteraction);
            })
            .catch(e => {
              console.error("Play failed even after user interaction:", e);
              // Keep listeners if play fails
            });
        }
      };

      // Add listeners proactively for iOS
      document.addEventListener('click', handleAnyInteraction);
      document.addEventListener('touchstart', handleAnyInteraction);
      document.addEventListener('scroll', handleAnyInteraction, { passive: true });

      // Also try to play immediately
      if (playerRef.current?.paused) {
        console.log("Should be playing but paused, attempting to play");
        playerRef.current.play().catch(err => {
          console.log("Auto-play attempt failed:", err);
        });
      }

      // Return cleanup function
      return () => {
        document.removeEventListener('click', handleAnyInteraction);
        document.removeEventListener('touchstart', handleAnyInteraction);
        document.removeEventListener('scroll', handleAnyInteraction);
      };
    }
  }, [inView, isReady, props.isScrolling, props.scrollSpeed]);

  const updatePlayerWidth = useCallback(() => {
    const width = props.isHorizontal || props.isExternal ? "100%" : getWidthForVerticalVideo();
    setPlayerWidth(width);
  }, [props.isHorizontal, props.isExternal]);

  useEffect(() => {
    updatePlayerWidth();
    window.addEventListener("resize", updatePlayerWidth);
    return () => {
      window.removeEventListener("resize", updatePlayerWidth);
    };
  }, [updatePlayerWidth]);

  const getWidthForVerticalVideo = () => {
    const width = document.documentElement.clientWidth;
    if (width < 400) return "65%";
    if (width < 640) return "50%";
    if (width < 768) return "55%";
    if (width < 1024) return "50%";
    if (width < 1280) return "45%";
    if (width < 1536) return "40%";
    return "40%";
  }

  const handleCanPlay = () => {
    setIsReady(true);
  };

  const handleError = (event: any) => {
    console.error("Vidstack Player Error:", event.detail);
  }

  if (!props.src || threshold === undefined) {
    return null;
  }

  const handleMuteClick = () => {
    setMuted(!isMuted);
  };

  function onProviderChange(provider: MediaProviderAdapter | null) {
    if (!provider) {
      return;
    }

    if (isYouTubeProvider(provider)) {
      provider.referrerPolicy = 'no-referrer-when-downgrade';
      provider.iframe.referrerPolicy = 'no-referrer-when-downgrade';
    }
  }

  const handleMediaClick = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  }

  return (
    <div className={"flex flex-col items-center"} ref={combinedRef}>
      <VideoProvider playerRef={playerRef}>
        <MediaPlayer
          ref={playerRef}
          src={src}
          poster={props.thumbnail}
          aspectRatio={aspectRatioFraction}
          playsInline
          {...(platform == "android" && { crossOrigin: "" })}
          loop
          paused={!shouldBePlaying}
          onCanPlay={handleCanPlay}
          onError={handleError}
          load="custom"
          posterLoad="eager"
          style={{ width: playerWidth }}
          onProviderChange={onProviderChange}
          className={loadingClass}
          onClick={handleMediaClick}
          controlsDelay={hasInteracted ? 1250 : 0}
          muted={isMuted || !!props.forceMuted}
        >
          <MediaProvider>
            {props.subtitles && (
              <Track
                src={props.subtitles}
                kind="subtitles"
                label="English"
              />
            )}
            {props.thumbnail && !props.isExternal && (
              <Poster
                className={"absolute inset-0 block h-full w-full rounded-md opacity-0 transition-opacity data-[visible]:opacity-100 [&>img]:h-full [&>img]:w-full [&>img]:object-cover"}
                src={props.thumbnail}
              />
            )}
          </MediaProvider>
          <FullscreenPlayControl hasInteracted={hasInteracted} />
          <YoutubePlayerControl src={props.src} thumbnail={props.thumbnail} isExternal={props.isExternal} isNative={isNative} />
          <DefaultVideoLayout
            smallLayoutWhen={props.isExternal ? false : true}
            icons={defaultLayoutIcons}
            slots={{
              smallLayout: {
                playButton: null,
                airPlayButton: <CustomSettingsMenu hasInteracted={hasInteracted} />,
                bufferingIndicator: null,
                muteButton: props.isExternal ? <CustomMuteButton hasInteracted={hasInteracted} mutedVolume={isMuted} handleMuteButtonClick={handleMuteClick} /> : null,
                settingsMenu: props.isExternal ? null : <CustomMuteButton hasInteracted={hasInteracted} mutedVolume={isMuted} handleMuteButtonClick={handleMuteClick} />,
              },
              largeLayout: {
                settingsMenu: <CustomSettingsMenu hasInteracted={hasInteracted} />,
                bufferingIndicator: null,
                muteButton: <CustomMuteButton hasInteracted={hasInteracted} mutedVolume={isMuted} handleMuteButtonClick={handleMuteClick} />,
              },
            }}
          />
        </MediaPlayer>
      </VideoProvider>
    </div>
  );
};

export default AutoVideoPlayer;